import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Briefcase, ArrowLeft, CheckCircle2, Upload, Loader2 } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/seo/Seo";

export default function CareersDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "" });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/jobs/${slug}`)
      .then((r) => setJob(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("CV exceeds 8MB limit");
      return;
    }
    setCv(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!cv) return toast.error("Please upload your CV (PDF or DOCX).");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("cover_letter", form.cover_letter);
      fd.append("cv", cv);
      await api.post(`/jobs/${slug}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
      toast.success("Application received. We'll be in touch within 5 business days.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-32 text-center text-muted-foreground">Loading…</div>;
  if (notFound || !job) return (
    <div className="max-w-5xl mx-auto px-6 py-32 text-center" data-testid="careers-not-found">
      <Seo title="Role not found" noindex />
      <h1 className="font-heading font-bold text-3xl">Role not found</h1>
      <Link to="/careers" className="mt-6 inline-flex items-center gap-2 text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to careers
      </Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-20" data-testid="careers-detail-page">
      <Seo title={`${job.title} — Careers`} description={job.summary} />
      <Link to="/careers" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors" data-testid="careers-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to careers
      </Link>

      <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1 text-muted-foreground"><Briefcase className="w-3 h-3" /> {job.department}</span>
        <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin className="w-3 h-3" /> {job.location}</span>
        <span className="px-2 py-0.5 rounded-full bg-muted font-mono uppercase tracking-[0.15em]">{job.type}</span>
        {job.remote && <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono uppercase tracking-[0.15em]">Remote</span>}
      </div>

      <h1 className="mt-5 font-heading font-bold text-4xl md:text-5xl tracking-tight leading-[1.05]" data-testid="job-title">
        {job.title}
      </h1>
      <p className="mt-5 text-lg text-muted-foreground max-w-3xl leading-relaxed">{job.summary}</p>

      <div className="mt-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          <div>
            <h2 className="font-heading font-bold text-2xl leading-tight">About the role</h2>
            <p className="mt-3 text-base leading-relaxed text-foreground/85 whitespace-pre-line">{job.description}</p>
          </div>

          {job.requirements?.length > 0 && (
            <div>
              <h2 className="font-heading font-bold text-2xl leading-tight">What you bring</h2>
              <ul className="mt-4 space-y-2">
                {job.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm md:text-base">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-foreground/85">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {job.benefits?.length > 0 && (
            <div>
              <h2 className="font-heading font-bold text-2xl leading-tight">Benefits</h2>
              <ul className="mt-4 space-y-2">
                {job.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm md:text-base">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-foreground/85">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Apply form */}
        <aside className="lg:col-span-5">
          <div className="card-clean p-6 sticky top-32" data-testid="apply-form-card">
            {submitted ? (
              <div className="text-center py-8" data-testid="apply-success">
                <CheckCircle2 className="w-12 h-12 mx-auto text-primary" />
                <h3 className="mt-4 font-heading font-bold text-xl">Application received</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Thanks {form.name.split(" ")[0]}. We review every application. You&apos;ll hear from us within 5 business days.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="apply-form">
                <h3 className="font-heading font-bold text-xl">Apply for this role</h3>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Full name *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="apply-name" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Email *</label>
                  <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="apply-email" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="apply-phone" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Cover letter</label>
                  <textarea value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                    rows={4}
                    placeholder="Why you, why CloudLab, why this role?"
                    className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none"
                    data-testid="apply-cover" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">CV (PDF or DOCX, max 8MB) *</label>
                  <div className="mt-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={onFile}
                      className="hidden"
                      data-testid="apply-cv-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="w-full border border-dashed border-border rounded-xl px-4 py-4 flex items-center justify-center gap-2 text-sm hover:border-primary/40 transition-colors"
                      data-testid="apply-cv-button"
                    >
                      <Upload className="w-4 h-4" />
                      {cv ? cv.name : "Choose file"}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-primary text-primary-foreground text-sm font-medium py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="apply-submit"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit application"}
                </button>
                <p className="text-[11px] text-muted-foreground text-center">
                  By applying, you agree to our privacy policy. We&apos;ll only use your data to process this application.
                </p>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
